import { Icon, InlineLink, Text } from "@assetwatch/ui";
import type { ReactNode } from "react";
import type { CmeAsset } from "./api";
import {
  assetPageUrl,
  calendarDateFromParam,
  formatRpm,
  PREDICTED_1X_DAYS,
} from "./lib";

const DETECTED_FORMAT = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  year: "numeric",
});

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt>
        <Text size="sm" tone="subtle">
          {label}
        </Text>
      </dt>
      <dd className="m-0 min-w-0 truncate">
        <Text weight="medium">{children}</Text>
      </dd>
    </>
  );
}

/**
 * Left box of the feedback pane: what's being reviewed, as a short label /
 * value list centered in the box. Today that's a monitoring point on a
 * machine, linked to its AssetWatch Web page.
 */
export function FeedbackItemDetails({
  asset,
  predicted1x,
  rpmPending,
}: {
  asset: CmeAsset;
  /** Week-median RPM in Hz; null when there were no RPM readings. */
  predicted1x: number | null;
  rpmPending: boolean;
}) {
  const machine = asset.machine || "Unknown machine";
  // Built on the page (env-aware host, Analysis tab, scrolled to the point);
  // the API's older prod-only link is the fallback.
  const href = assetPageUrl(asset) ?? asset.url;
  return (
    <div className="flex flex-1 flex-col justify-center">
      <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-(--spacing-16) gap-y-(--spacing-12)">
        <Detail label="Machine">
          {href ? (
            <InlineLink href={href} rel="noopener noreferrer" target="_blank">
              <span className="inline-flex items-center gap-(--spacing-4)">
                {machine}
                <Icon name="arrow-up-right" size="sm" tone="brand" />
              </span>
            </InlineLink>
          ) : (
            machine
          )}
        </Detail>
        <Detail label="Customer">{asset.customer_name || "Unknown"}</Detail>
        <Detail label="Facility">{asset.facility || "Unknown"}</Detail>
        <Detail label="Point">{asset.monitoring_point || "Unknown"}</Detail>
        <Detail label="Detected">
          {DETECTED_FORMAT.format(calendarDateFromParam(asset.detected_date))}
        </Detail>
        <Detail label="Predicted 1X">
          {rpmPending
            ? "Loading…"
            : predicted1x === null
              ? "No RPM data"
              : `${formatRpm(predicted1x)} (${PREDICTED_1X_DAYS}-day median)`}
        </Detail>
      </dl>
    </div>
  );
}
